import { useState, useEffect } from "react";
import Button from "@/components/atoms/Button";
import Ticket from "@/types/Ticket";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import processQaSchema from "@/schemas/processQa";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import CKEditorWithFileUpload from "@/components/atoms/CKEditor";
import useAccess from "@/hooks/useAccess";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { router } from "@inertiajs/react";

type Props = {
  ticket: Ticket;
  qualityTickets: any[];
};

const ProcessQualityFrom = ({ ticket, qualityTickets }: Props) => {
  const processQaTicket = qualityTickets.filter((qa) => qa.passed !== null).pop() || null;
  const technicalQaTickets = qualityTickets.filter((qa) => qa.passed === null && qa.issue !== null) || null;
  const [editorData, setEditorData] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { currentUser, getAccess } = useAccess();

  const form = useForm({
    resolver: zodResolver(processQaSchema),
    defaultValues: {
      qa_issue: "",
      qa_external: "",
      qa_comment: ""
    }
  });

  const formatDate = (input: string): string => {
    const date = new Date(input.replace(/(\d+)\/(\d+)\/(\d+)/, '$2/$1/$3'));

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);

    const payload = {
      ticket: {
        qa_issue: data.qa_issue === "true",
        qa_external: data.qa_external === "" ? null : data.qa_external === "true",
        qa_comment: data.qa_comment
      }
    };
   
    try {
      router.patch(
        `/tickets/${ticket.id}/process_qa`,
        payload,
        {
          onSuccess: (page) => {
            setEditorData("");
            form.reset({
              qa_issue: "",
              qa_external: "",
              qa_comment: "",
            });
          },
          onError: (errors) => {
            setIsSubmitting(false);
          },
          onFinish: () => {
            setIsSubmitting(false);
          },
        }
      );
    } catch (error) {
      setIsSubmitting(false);
    }
  }

  const handleEditorChange = (data: string) => {
    setEditorData(data);
    form.setValue('qa_comment', data);
  };

  return (
    <div>
      {getAccess(["ticket_process_qa"]) ? (
        <>
          {processQaTicket ? (
            <>
              <div className="shadow-lg rounded-md p-6 w-full">
                <div className='bg-gray-100 dark:bg-background shadow-md rounded-md border border-border'>

                  <div className="flex-col gap-4 py-8 px-4">
                    <div className='flex items-start'>
                      <div className='w-60'>
                        <label className="text-sm text-gray-500 dark:text-darkTextColor">Comment</label>
                      </div>
                      <div className='bg-white dark:bg-background shadow-xs rounded-md w-full border border-border'>
                        <div className='py-4 px-4'>
                          <div className="mb-4">
                            <div className="flex items-center space-x-3">
                              <img
                                src={"avatar_url" in processQaTicket.passed_by ? (processQaTicket.passed_by.avatar_url) : "/default-avatar.png"}
                                alt="Profile"
                                className="w-8 h-8 rounded-full dark:bg-darkTextColor"
                              />
                              <div className='flex gap-2 items-center'>
                                <div>
                                  <h4 className="font-semibold text-gray-800 dark:text-darkTextColor">{processQaTicket.passed_by.name}</h4>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500 dark:text-darkTextColor">{formatDate(processQaTicket?.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4">
                            <div className="mt-4 ml-12">
                              <div
                                className="text-gray-700 dark:text-darkTextColor"
                                dangerouslySetInnerHTML={{ __html: processQaTicket.comment }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>

          ) : (
            <>
              {technicalQaTickets && technicalQaTickets.length > 0 && ticket.status.name === 'CLOSED' ? (
                <div className="shadow-lg rounded-md p-6 w-full">
                  <div className='bg-gray-100 dark:bg-background shadow-md rounded-md border border-border'>
                    <div className="px-4 pb-4">
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">

                          {ticket.account.external_quality_score ? (
                            <>
                              <input
                                type="hidden"
                                name="qa_issue"
                                value={currentUser ? currentUser.id.toString() : ''}
                                ref={(input) => {
                                  if (input) {
                                    form.setValue("qa_issue", "true");
                                  }
                                }}
                              />

                              <input
                                type="hidden"
                                name="qa_external"
                                value={currentUser ? currentUser.id.toString() : ''}
                                ref={(input) => {
                                  if (input) {
                                    form.setValue("qa_external", "true");
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <FormField
                              control={form.control}
                              name="qa_issue"
                              render={({ field }) => (
                                <FormItem className="w-1/5">
                                  <FormLabel>Was QA process successful? *</FormLabel>
                                  <FormControl>
                                    <RadioGroup.Root
                                      className="flex gap-4 mt-2"
                                      value={field.value.toString()}
                                      onValueChange={(value) => field.onChange(value)}
                                    >
                                      <RadioGroup.Item
                                        value="true"
                                        id="qa_yes"
                                        className="w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center cursor-pointer 
                                              focus:outline-none focus:ring-2 focus:ring-primary data-[state=checked]:bg-primary"
                                      >
                                        <RadioGroup.Indicator className="w-3 h-3 rounded-full bg-white" />
                                      </RadioGroup.Item>
                                      <label htmlFor="qa_yes" className="text-sm cursor-pointer">
                                        Yes
                                      </label>

                                      <RadioGroup.Item
                                        value="false"
                                        id="qa_no"
                                        className="w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center cursor-pointer 
                                              focus:outline-none focus:ring-2 focus:ring-primary data-[state=checked]:bg-primary"
                                      >
                                        <RadioGroup.Indicator className="w-3 h-3 rounded-full bg-white" />
                                      </RadioGroup.Item>
                                      <label htmlFor="qa_no" className="text-sm cursor-pointer">
                                        No
                                      </label>
                                    </RadioGroup.Root>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          <FormField
                            control={form.control}
                            name="qa_comment"
                            render={({ field }) => (
                              <FormItem className="w-full">
                                <FormLabel></FormLabel>
                                <FormControl>
                                  <CKEditorWithFileUpload
                                    value={editorData}
                                    onChange={handleEditorChange}
                                    isPublic={true}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="flex gap-2">
                            <Button type="submit" disabled={isSubmitting}>
                              Save
                            </Button>
                          </div>
                        </form>
                      </Form>

                    </div>
                  </div>
                </div>
              ) : (
                <div>This ticket is not suitable to do Process QA</div>
              )}
            </>
          )}
        </>
      ) : (
        <div>You have not access to show and perform Process QA</div>
      )}

    </div>
  );
};

export default ProcessQualityFrom;